import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, DownloadIcon, FileEditIcon, RefreshCwIcon } from 'lucide-react';
import { HeroComposition } from './HeroComposition';
const ease = [0.22, 1, 0.36, 1] as const;
const HIGHLIGHTS = [{
  icon: DownloadIcon,
  label: 'Descarga inmediata'
}, {
  icon: FileEditIcon,
  label: 'Formatos editables'
}, {
  icon: RefreshCwIcon,
  label: 'Actualizaciones incluidas'
}];
export function Hero() {
  return <section id="inicio" className="relative w-full overflow-hidden bg-white pb-24 pt-32 sm:pb-28 sm:pt-40 lg:pb-36 lg:pt-44" data-id="element-131" data-mp="127ed7af3pjbv">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" data-id="element-132" data-mp="sai535t46z7u">
        <div className="absolute inset-0 grid-lines opacity-70 [mask-image:radial-gradient(120%_80%_at_50%_0%,black,transparent_72%)]" data-id="element-133" data-mp="ncgkggg1iru9" />
        <div className="absolute -top-32 left-1/2 h-[520px] w-[900px] -translate-x-1/2 rounded-full blur-[120px]" style={{
        background: 'radial-gradient(circle at 38% 42%, rgba(37,99,246,0.16), transparent 62%), radial-gradient(circle at 66% 56%, rgba(122,77,255,0.16), transparent 62%)'
      }} data-id="element-134" data-mp="3d2bkbqsid1w2" />
      </div>

      <div className="relative mx-auto grid w-full max-w-content grid-cols-1 gap-y-12 px-6 lg:grid-cols-[1fr_1.05fr] lg:items-center lg:gap-x-10 lg:gap-y-0 lg:px-8" data-id="element-135" data-mp="39aupl8xknev4">
        {/* Titular */}
        <div className="lg:col-start-1 lg:row-start-1" data-id="element-136" data-mp="30iayctjm0ckb">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease
        }} className="inline-flex items-center gap-2 rounded-full border border-ink/[0.08] bg-paper-alt px-3.5 py-1.5" data-id="element-137" data-mp="30gq47517ivmz">
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full brand-gradient-bg" data-id="element-138" data-mp="2vjg5eyrxh7mh" />
            <span className="text-[12px] font-medium uppercase tracking-[0.18em] text-ink-soft sm:text-[12.5px]" data-id="element-139" data-mp="2hljas7x1bbag">
              Tienda de recursos digitales
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.08,
          ease
        }} className="mt-6 max-w-[17ch] text-[2.4rem] font-semibold leading-[1.05] tracking-[-0.03em] text-ink sm:mt-7 sm:text-6xl lg:text-[4rem]" data-id="element-140" data-mp="s3undmopqq5v">
            Recursos digitales{' '}
            <span className="gradient-text" data-id="element-141" data-mp="1b0dts484tzyq">
              para aprender, crear y emprender.
            </span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.16,
          ease
        }} className="mt-5 max-w-xl text-[16px] font-light leading-relaxed text-ink-soft sm:mt-7 sm:text-[17px]" data-id="element-142" data-mp="112px38qyuidx">
            eBooks, guías, plantillas y herramientas descargables sobre
            distintas temáticas, listas para usar desde el primer día.
          </motion.p>
        </div>

        {/* Composición visual */}
        <div className="relative order-2 -mx-2 lg:order-none lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:mx-0 lg:self-center" data-id="element-143" data-mp="21amx7d2zpynx">
          <div className="mx-auto w-full max-w-[420px] sm:max-w-[520px] lg:max-w-none" data-id="element-144" data-mp="268y69llgo553">
            <HeroComposition data-id="element-145" data-mp="2b0ze8vblj9pb" />
          </div>
        </div>

        {/* Acciones */}
        <div className="order-3 lg:order-none lg:col-start-1 lg:row-start-2" data-id="element-146" data-mp="2g02hqbbxh7v6">
          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.24,
          ease
        }} className="flex flex-col gap-3.5 sm:flex-row sm:items-center lg:mt-12" data-id="element-147" data-mp="2g25bn6g7bl56">
            <a href="#productos" className="group brand-gradient-bg inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium text-white shadow-[0_20px_46px_-18px_rgba(90,80,246,0.7)] transition-all duration-300 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric/50 focus-visible:ring-offset-2" data-id="element-148" data-mp="2tz1zcz10wmhu">
              Explorar Productos
              <ArrowRightIcon aria-hidden="true" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" data-id="element-149" data-mp="e9aaaealvgpw" />
            </a>
            <a href="#nosotros" className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/[0.10] bg-white px-6 py-3.5 text-sm font-medium text-ink shadow-soft transition-colors duration-300 hover:border-ink/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric/40" data-id="element-150" data-mp="3hq0tjuv77q9j">
              Conocer Más
            </a>
          </motion.div>

          <motion.ul initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="mt-12 flex flex-wrap gap-x-8 gap-y-4 border-t border-ink/[0.08] pt-9 sm:mt-16" data-id="element-151" data-mp="2yyr6sjwf3500">
            {HIGHLIGHTS.map(item => <li key={item.label} className="flex items-center gap-2.5" data-id="element-152" data-mp="1eaf9kd8fayqa">
                <span className="grid h-8 w-8 place-items-center rounded-xl border border-ink/[0.08] bg-paper-alt" data-id="element-153" data-mp="zkznhaju7hgx">
                  <item.icon aria-hidden="true" className="h-4 w-4 text-electric" strokeWidth={1.6} data-id="element-154" data-mp="14km29ofbd5ru" />
                </span>
                <span className="text-[13.5px] font-light text-ink-soft" data-id="element-155" data-mp="1x76t8nrvnfuv">
                  {item.label}
                </span>
              </li>)}
          </motion.ul>
        </div>
      </div>
    </section>;
}